import type {
	AnswerDto,
	DamageCodesDto,
	GradingDto,
	InsuranceClaimDtoV2,
	OrderResponseDto,
	QuestionsDto,
	SubmitDto,
} from "~/ts-integrations/device-return-v1";
import apigeeIntegration from "./self-service-adapter";
import systemUserIntegration from "./self-service-system-adapter";
import type { DeviceInfo } from "~/ts-integrations/device-return-v2";

const api = apigeeIntegration("/device-return/v1");
const apiV2 = apigeeIntegration("/device-return/v2");
const systemUserApi = systemUserIntegration("/device-return/v1");
const systemUserApiV2 = systemUserIntegration("/device-return/v2");

// V1 Docs: https://test-developer.telenor.net/documentation/apis/device-return-v1/atest-nx
// V2 Docs: https://test-developer.telenor.net/documentation/apis/device-return-v2/atest-nx

const DISABLE_CACHE = {
	headers: {
		"x-apicache-bypass": true,
	},
	cache: { override: true },
};

const searchIMEINumber = async (imeiNumber: number | string): Promise<Array<DeviceInfo>> =>
	systemUserApiV2.get(`/return/devices/${imeiNumber}?details=true`, {
		headers: {
			"x-telenor-dealer-id": 6662,
			"x-telenor-channel-name": "MTW",
		},
		timeout: 30000,
	});

const searchIMEINumberLoggedInUser = async (imeiNumber: number | string): Promise<Array<DeviceInfo>> =>
	apiV2.get(`/return/devices/${imeiNumber}?details=true`);

const getGradingQuestions = async (language: "en" | "no" = "no"): Promise<QuestionsDto> =>
	systemUserApi.get(`/questions/${language}`, { timeout: 30000 });

const getEstimatedValue = async (imeiNumber: number | string, device: AnswerDto): Promise<GradingDto> =>
	systemUserApi.post(`/${imeiNumber}/grading`, device, { ...DISABLE_CACHE, timeout: 30000 });

const getEstimatedValueLoggedInUser = async (imeiNumber: number | string, device: AnswerDto): Promise<GradingDto> =>
	api.post(`/${imeiNumber}/grading`, device, { timeout: 30000 });

const getDamageCodes = async (deviceReturnId: number | string, language: "en" | "no" = "no"): Promise<DamageCodesDto> =>
	systemUserApi.get(`/${deviceReturnId}/damagecodes/${language}`, { timeout: 30000 });

const getDamageCodesLoggedInUser = async (
	deviceReturnId: number | string,
	language: "en" | "no" = "no",
): Promise<DamageCodesDto> => api.get(`/${deviceReturnId}/damagecodes/${language}`, { timeout: 30000 });

const submitInsuranceClaim = async (
	deviceReturnId: number | string,
	insuranceClaim: InsuranceClaimDtoV2,
): Promise<unknown> => api.post(`/${deviceReturnId}/insuranceclaim`, insuranceClaim, { timeout: 30000 });

const submitOrder = async (deviceReturnId: number | string, order: SubmitDto): Promise<OrderResponseDto> =>
	api.post(`/${deviceReturnId}/submit?sendContractByMail=true&autoPostContractToPartner=true`, order, {
		timeout: 30000,
	});

const updateGrading = async (deviceReturnId: number | string, gtin: string | number): Promise<unknown> =>
	api.post(
		`/${deviceReturnId}/campaignbonus?updateGrading=true`,
		{ gtin: gtin },
		{
			headers: {
				"x-telenor-dealer-id": 6662,
				"x-telenor-channel-name": "MineSider",
			},
			timeout: 30000,
		},
	);

const cancelOrder = async (deviceReturnOrderId: string | number, reason: string): Promise<unknown> =>
	apiV2.delete(`/return/orders/${deviceReturnOrderId}?reason=${reason}`, {
		headers: {
			"x-telenor-dealer-id": 6662,
			"x-telenor-channel-name": "MineSider",
		},
		timeout: 30000,
	});

export default {
	searchIMEINumber,
	searchIMEINumberLoggedInUser,
	getGradingQuestions,
	getEstimatedValue,
	getEstimatedValueLoggedInUser,
	getDamageCodes,
	getDamageCodesLoggedInUser,
	submitInsuranceClaim,
	submitOrder,
	updateGrading,
	cancelOrder,
};
